/**
 * Account activation, against ThinLMS's own endpoint rather than the platform's page.
 *
 * The platform activates at `GET /activate/<key>` on the LMS host. Linking an email there
 * sends a Skill Our Future learner to a generic platform page for the first message they
 * ever receive — and pointing that same link at the academy's own host does nothing at
 * all, because this SPA serves that host and had no `/activate` route, so the URL returned
 * the app shell and the account stayed inactive.
 *
 * So activation is performed by `api/account/v1/activate/` and this app renders the
 * result under the academy's branding.
 *
 * THE ACTIVATION CALLS ARE ANONYMOUS ON PURPOSE. Nobody can be signed in yet — that is the
 * entire point of activating — and the link is usually opened in whatever browser the mail
 * client hands it to, very often a private window with no session. `allowUnauthenticated`
 * keeps a missing session from being treated as an error.
 *
 * `resendActivation` is the exception and is AUTHENTICATED, because by then the learner
 * does have a session: registration signs you in before you verify. It takes no address —
 * the server uses the one on the session's account — so it cannot be used as a relay.
 */

import { ApiError, request } from './openedx';

export type ActivationStatus =
  /** The account was inactive and has just been activated by this call. */
  | 'activated'
  /** Already done — a second click, a re-opened email, or a mail client prefetch. */
  | 'already_active'
  /** Still waiting; only returned by the read-only status check. */
  | 'pending'
  /** No such key. Also what a truncated or mangled link produces. */
  | 'invalid';

const BASE = '/api/account/v1/activate/';

/** Read-only: what would happen, without making it happen. */
export async function checkActivation(key: string): Promise<ActivationStatus> {
  try {
    const data = await request<{ status: ActivationStatus }>(
      `${BASE}status/?key=${encodeURIComponent(key)}`,
      { allowUnauthenticated: true },
    );
    return data?.status ?? 'invalid';
  } catch {
    // A 404 is a real answer here ("no such key"), and every other failure is
    // indistinguishable from it to the learner, so both render the same page.
    return 'invalid';
  }
}

/**
 * Is the SIGNED-IN account verified?
 *
 * `is_active` rides along on the account payload the platform already serves, so this
 * needs no bespoke endpoint. Returns `null` when it cannot tell — an anonymous visitor,
 * or a failed request — and callers must treat `null` as "say nothing", never as
 * "unverified". Accusing a verified learner of not having verified is worse than
 * staying quiet.
 */
export async function isAccountVerified(username: string): Promise<boolean | null> {
  if (!username) return null;
  try {
    const account = await request<{ is_active?: boolean }>(
      `/api/user/v1/accounts/${encodeURIComponent(username)}`,
      { allowUnauthenticated: true },
    );
    if (!account || typeof account.is_active !== 'boolean') return null;
    return account.is_active;
  } catch {
    return null;
  }
}

export type ResendResult = 'sent' | 'already_active' | 'throttled' | 'failed';

/**
 * Ask for the activation email again.
 *
 * Authenticated and takes no address: the server sends to whatever the session's account
 * holds. There is deliberately no way to name a recipient — that would make it a relay.
 *
 * `throttled` is surfaced separately from `failed` because the two need different words.
 * "Try again in a while" is actionable; "something went wrong" for a rate limit sends
 * people to support for no reason.
 */
export async function resendActivation(): Promise<ResendResult> {
  try {
    const data = await request<{ status?: string }>('/api/account/v1/activate/resend/', {
      method: 'POST',
      json: {},
    });
    if (data?.status === 'already_active') return 'already_active';
    if (data?.status === 'sent') return 'sent';
    return 'failed';
  } catch (error) {
    if (error instanceof ApiError && error.status === 429) return 'throttled';
    return 'failed';
  }
}

/**
 * Perform the activation.
 *
 * POST, not GET, and that is deliberate: link-scanning proxies (Outlook Safe Links,
 * corporate antivirus, mail preview fetchers) follow URLs before a human clicks. Against
 * the platform's GET route that silently activates accounts. Here a scanner only loads
 * this page; nothing changes until this call runs.
 */
export async function activateAccount(key: string): Promise<ActivationStatus> {
  try {
    const data = await request<{ status: ActivationStatus }>(BASE, {
      method: 'POST',
      json: { key },
      allowUnauthenticated: true,
    });
    return data?.status ?? 'invalid';
  } catch {
    return 'invalid';
  }
}
